import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Elemental cost analyses — a tagged DOCUMENT library.
 *
 * Separate from datasetApi because this register holds files rather than
 * figures: there is no value, no unit, no natural key, and the payloads are
 * multipart. See the migration on the API side for why it is its own module.
 *
 * The file itself is never a plain URL. It is private, and `download_path`
 * points at an authenticated endpoint that streams it — so downloading goes
 * through the same fetch (and the same token) as everything else.
 */

export type ElementalCostDocument = {
  id: number;
  project_description: string | null;
  state_id: number;
  state: string | null;
  client_type_id: number;
  client_type: string | null;
  project_type_id: number;
  project_type: string | null;
  project_category_id: number;
  project_category: string | null;
  file_name: string;
  file_size: number;
  mime_type: string;
  // False when the index was registered before the document itself was copied
  // to the server. The entry is real; the file is not there yet.
  file_available: boolean;
  download_path: string;
  is_approved: boolean;
  uploaded_at: string | null;
};

export type ElementalCostPage = {
  data: ElementalCostDocument[];
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
};

type Tag = { id: number; name: string };

export type ElementalCostFilters = {
  states: Tag[];
  client_types: Tag[];
  project_types: Tag[];
  project_categories: Tag[];
  meta: {
    label: string;
    accepts: string[];
    max_kilobytes: number;
    total_documents: number | null;
  };
};

type ListArgs = {
  state_id?: string | number;
  client_type_id?: string | number;
  project_type_id?: string | number;
  project_category_id?: string | number;
  q?: string;
  cursor?: string | null;
  per_page?: number;
  include_unapproved?: boolean;
};

const LIST_KEYS = [
  "state_id", "client_type_id", "project_type_id", "project_category_id",
  "q", "cursor", "per_page", "include_unapproved",
];

function qs(args: Record<string, any>) {
  const p = new URLSearchParams();
  LIST_KEYS.forEach((k) => {
    const v = args[k];
    if (v !== undefined && v !== null && String(v) !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const elementalCostApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getElementalCostDocuments: b.query<ElementalCostPage, ListArgs>({
      query: (a) => `/databank/elemental-costs${qs(a)}`,
      providesTags: ["ElementalCostDocuments"],
    }),

    getElementalCostFilters: b.query<ElementalCostFilters, void>({
      query: () => "/databank/elemental-costs/filters",
      providesTags: ["ElementalCostFilters"],
    }),

    // Multipart: the tags and the file go up together.
    uploadElementalCostDocument: b.mutation<any, FormData>({
      query: (body) => ({ url: "/admin/databank/elemental-costs", method: "POST", body }),
      invalidatesTags: ["ElementalCostDocuments", "ElementalCostFilters"],
    }),

    // Also multipart — a file may or may not be attached; without one this is
    // a re-tag and the stored document is left alone.
    updateElementalCostDocument: b.mutation<any, { id: number; body: FormData }>({
      query: ({ id, body }) => {
        // Laravel does not parse multipart on PUT, so the method is spoofed —
        // the route is still PUT and nothing downstream has to know.
        body.append("_method", "PUT");
        return { url: `/admin/databank/elemental-costs/${id}`, method: "POST", body };
      },
      invalidatesTags: ["ElementalCostDocuments", "ElementalCostFilters"],
    }),

    deleteElementalCostDocument: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/databank/elemental-costs/${id}`, method: "DELETE" }),
      invalidatesTags: ["ElementalCostDocuments", "ElementalCostFilters"],
    }),

    // Registers files ALREADY copied onto the server, from a CSV of the old
    // table. Not an upload of the documents themselves.
    importElementalCostIndex: b.mutation<any, FormData>({
      query: (body) => ({ url: "/admin/databank/elemental-costs/import", method: "POST", body }),
      invalidatesTags: ["ElementalCostDocuments", "ElementalCostFilters"],
    }),
  }),
});

export const {
  useGetElementalCostDocumentsQuery,
  useGetElementalCostFiltersQuery,
  useUploadElementalCostDocumentMutation,
  useUpdateElementalCostDocumentMutation,
  useDeleteElementalCostDocumentMutation,
  useImportElementalCostIndexMutation,
} = elementalCostApi;
